import { useState } from 'react';
import { useTheme } from '../lib/theme-provider';
import { ThemedCard } from './ThemedCard';
import { ThemedButton } from './ThemedButton';
import { Sword, X, Shield, RotateCcw } from 'lucide-react';

interface Player {
  id: string;
  seatNumber: number;
  nickname: string;
  missionResult: 'success' | 'fail';
}

interface ExcaliburModalProps {
  isOpen: boolean;
  players: Player[];
  onClose: () => void;
  onReverse: (targetSeat: number) => void;
}

export default function ExcaliburModal({
  isOpen,
  players,
  onClose,
  onReverse,
}: ExcaliburModalProps) {
  const { theme } = useTheme();
  const [selectedSeat, setSelectedSeat] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);

  if (!isOpen) return null;

  // 过滤出执行任务的玩家
  const missionPlayers = players.filter((p) => p.missionResult);

  const handleSelect = (seatNumber: number) => {
    setSelectedSeat(seatNumber);
    setShowResult(false);
  };

  const handleReverse = () => {
    if (selectedSeat === null) return;
    onReverse(selectedSeat);
    setShowResult(true);
  };

  const selectedPlayer = players.find((p) => p.seatNumber === selectedSeat);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(4px)' }}
    >
      <ThemedCard className="w-full max-w-md p-6">
        {/* Header */}
        <div className="flex items-center justify-center mb-4">
          <div
            className="flex items-center gap-2"
            style={{ color: theme.colors['accent-gold'] }}
          >
            <Sword size={24} />
            <h2
              className="text-xl font-bold"
              style={{ color: theme.colors['accent-gold'] }}
            >
              王者之剑
            </h2>
          </div>
        </div>

        {/* 说明 */}
        <div
          className="mb-6 p-3 rounded-lg text-center text-sm"
          style={{
            backgroundColor: `${theme.colors['accent-gold']}10`,
            border: `1px solid ${theme.colors['accent-gold']}30`,
            color: theme.colors['text-secondary'],
          }}
        >
          选择一名任务队员，可以反转其任务结果
          <br />
          成功变失败，失败变成功
        </div>

        {/* 玩家选择 */}
        {!showResult && (
          <div className="grid grid-cols-3 gap-3 mb-6">
            {missionPlayers.map((player) => (
              <button
                key={player.id}
                onClick={() => handleSelect(player.seatNumber)}
                className="flex flex-col items-center p-3 rounded-xl transition-all"
                style={{
                  backgroundColor:
                    selectedSeat === player.seatNumber
                      ? `${theme.colors['accent-gold']}30`
                      : theme.colors['bg-primary'],
                  border: `2px solid ${
                    selectedSeat === player.seatNumber
                      ? theme.colors['accent-gold']
                      : `${theme.colors['border-secondary']}40`
                  }`,
                }}
              >
                {/* 头像 */}
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold mb-2"
                  style={{
                    backgroundColor: theme.colors['accent-purple'],
                    color: theme.colors['bg-primary'],
                  }}
                >
                  {player.nickname[0]}
                </div>
                {/* 昵称 */}
                <span
                  className="text-xs font-medium truncate w-full text-center"
                  style={{ color: theme.colors['text-primary'] }}
                >
                  {player.nickname}
                </span>
                {/* 座位号 */}
                <span
                  className="text-xs"
                  style={{ color: theme.colors['text-muted'] }}
                >
                  #{player.seatNumber}
                </span>

                {/* 任务结果标记 */}
                <div
                  className="mt-1 px-2 py-0.5 rounded-full text-xs"
                  style={{
                    backgroundColor:
                      player.missionResult === 'success'
                        ? '#4ade80'
                        : '#f87171',
                    color: theme.colors['bg-primary'],
                  }}
                >
                  {player.missionResult === 'success' ? '成功' : '失败'}
                </div>

                {/* 已选择标记 */}
                {selectedSeat === player.seatNumber && (
                  <div
                    className="absolute -top-1 -right-1 px-2 py-0.5 rounded-full text-xs font-bold"
                    style={{
                      backgroundColor: theme.colors['accent-gold'],
                      color: theme.colors['bg-primary'],
                    }}
                  >
                    已选择
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        {/* 反转结果 */}
        {showResult && selectedPlayer && (
          <div
            className="mb-6 p-6 rounded-xl text-center"
            style={{
              backgroundColor: `${theme.colors['accent-gold']}15`,
              border: `2px solid ${theme.colors['accent-gold']}`,
            }}
          >
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl font-bold"
              style={{
                backgroundColor: theme.colors['accent-gold'],
                color: theme.colors['bg-primary'],
              }}
            >
              <RotateCcw size={32} />
            </div>
            <p
              className="text-lg font-bold mb-2"
              style={{ color: theme.colors['text-primary'] }}
            >
              {selectedPlayer.nickname} 的任务结果已被反转
            </p>
            <p
              className="text-sm mb-4"
              style={{ color: theme.colors['text-secondary'] }}
            >
              原结果: {selectedPlayer.missionResult === 'success' ? '成功' : '失败'}
            </p>
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-lg font-bold"
              style={{
                backgroundColor:
                  selectedPlayer.missionResult === 'success'
                    ? '#f87171'
                    : '#4ade80',
                color: theme.colors['bg-primary'],
              }}
            >
              <Sword size={20} />
              现在: {selectedPlayer.missionResult === 'success' ? '失败' : '成功'}
            </div>
          </div>
        )}

        {/* 操作按钮 */}
        <div className="flex gap-3">
          <ThemedButton variant="secondary" onClick={onClose} className="flex-1">
            <X size={18} className="mr-1" />
            {showResult ? '关闭' : '取消'}
          </ThemedButton>
          {!showResult && (
            <ThemedButton
              onClick={handleReverse}
              disabled={selectedSeat === null}
              className="flex-1"
              style={{
                backgroundColor: selectedSeat !== null ? theme.colors['accent-gold'] : undefined,
              }}
            >
              <RotateCcw size={18} className="mr-1" />
              反转结果
            </ThemedButton>
          )}
        </div>
      </ThemedCard>
    </div>
  );
}
